import React, { useEffect, useState, useRef } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Input,
  FormGroup,
  Progress,
} from "reactstrap";
import { axiosHelper, tokenHelper } from "../../../../stores/helpers";
import { toast } from "react-toastify";
import Select from "react-select";
import { useSiteStore } from "../../../../stores/Assets/site";
import { useSpaceStore } from "../../../../stores/Assets/space";
import { useSplashStore } from "../../../../stores/Assets/splash";
import {
  ConciergeBell,
  MapPin,
  Type,
  Zap,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Sparkles
} from "lucide-react";
import "./AddSplash.css";

const STEPS = [
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'content', title: 'Content', icon: Type },
  { id: 'action', title: 'Action', icon: Zap },
  { id: 'confirm', title: 'Confirm', icon: Check },
];

const AddSplash = ({ showAddSplashModal, toggleAddSplashModal }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [direction, setDirection] = useState('next'); // 'next' or 'prev' for animation
  const contentRef = useRef(null);

  // Data states
  const [sites, setSites] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [menus, setMenus] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);

  // Form state
  const [form, setForm] = useState({
    siteId: null,
    spaceIds: ["ALL"],
    title: "",
    subtitle: "Choose and Order ... We will bring it to your table.",
    menuButtonCaption: "Check menu",
    redirectToMenu: false,
    menuUrl: "",
    actionType: "CALL_WAITER", // CALL_WAITER | CUSTOM | NONE
    actionButtonText: "Call waiter",
    selectedMenuId: null,
    selectedMenuItemId: null,
    note: "",
  });

  const { getSites } = useSiteStore();
  const { getSpaces } = useSpaceStore();
  const { createSplash } = useSplashStore();
  const storeSpaces = useSpaceStore((s) => s.spaces);

  // Fetch sites on mount
  useEffect(() => {
    if (showAddSplashModal) {
      getSites().then((res) => {
        const list = res || [];
        setSites(list);
        if (list.length > 0 && !form.siteId) {
          update({ siteId: list[0].site_id });
        }
      });
    } else {
      // Reset on close
      setCurrentStep(0);
      setForm({
        siteId: null,
        spaceIds: ["ALL"],
        title: "",
        subtitle: "Choose and Order ... We will bring it to your table.",
        menuButtonCaption: "Check menu",
        redirectToMenu: false,
        menuUrl: "",
        actionType: "CALL_WAITER",
        actionButtonText: "Call waiter",
        selectedMenuId: null,
        selectedMenuItemId: null,
        note: "",
      });
    }
  }, [showAddSplashModal]);

  // Fetch spaces when site changes
  useEffect(() => {
    if (form.siteId) {
      getSpaces(form.siteId, { page: 1, perPage: 500 });
    }
  }, [form.siteId, getSpaces]);

  // Update spaces from store
  useEffect(() => {
    if (form.siteId && storeSpaces) {
      const filtered = storeSpaces.filter(
        (s) => s.site_id === form.siteId || s.site?.site_id === form.siteId
      );
      setSpaces(filtered);
      if (filtered.length > 0 && form.spaceIds.length === 0) {
        update({ spaceIds: ["ALL"] });
      }
    }
  }, [storeSpaces, form.siteId]);

  // Fetch menus
  useEffect(() => {
    if (!form.siteId) return;
    const fetchMenus = async () => {
      try {
        const token = await tokenHelper.getToken();
        const res = await axiosHelper.get(`/menu/site/${form.siteId}/service-menus`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMenus(res || []);
      } catch {
        setMenus([]);
      }
    };
    fetchMenus();
  }, [form.siteId]);

  // Fetch service items
  useEffect(() => {
    const fetchItems = async () => {
      if (!form.selectedMenuId) {
        setServiceItems([]);
        return;
      }
      try {
        const token = await tokenHelper.getToken();
        const res = await axiosHelper.get(`/menu/${form.selectedMenuId}/items?perPage=999`, {
          headers: { Authorization: `Bearer ${token}` },
          suppressToast: true,
        });
        setServiceItems((res?.items || []).filter((i) => i.type === "SERVICE"));
      } catch {
        setServiceItems([]);
      }
    };
    fetchItems();
  }, [form.selectedMenuId]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return !!form.siteId && form.spaceIds.length > 0;
      case 1:
        return true; // Content is optional
      case 2:
        return true; // Action is optional
      case 3:
        return true;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setDirection('next');
      setCurrentStep((s) => s + 1);
    } else {
      handleCreate();
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setDirection('prev');
      setCurrentStep((s) => s - 1);
    }
  };

  const handleCreate = async () => {
    const resolvedSpaceIds = form.spaceIds.includes("ALL")
      ? spaces.map((s) => s.space_id)
      : form.spaceIds;

    if (resolvedSpaceIds.length === 0) {
      toast.error(t("Please select at least one space"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle,
        menu_button_caption: form.menuButtonCaption,
        action__button_text: form.actionType !== "NONE" ? form.actionButtonText : null,
        service_action_type: form.actionType === "CALL_WAITER" ? "CALL_WAITER" : null,
        menu_url: form.redirectToMenu ? form.menuUrl : null,
        menu_item_id: form.actionType === "CUSTOM" ? form.selectedMenuItemId : null,
        space_ids: resolvedSpaceIds,
        note: form.note,
      };

      await createSplash(payload);
      toast.success(t("Splash created successfully!"));
      toggleAddSplashModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || t("Failed to create splash"));
    } finally {
      setSaving(false);
    }
  };

  // Progress percentage
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  // Selected site for preview
  const selectedSite = sites.find((s) => s.site_id === form.siteId);

  // Step content renderer
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <div className="step-header">
              <h4 className="step-title">{t("Where should this splash appear?")}</h4>
              <p className="step-description">{t("Select the site and spaces for this splash screen")}</p>
            </div>

            <FormGroup className="mb-4">
              <Label className="form-label-lg">{t("Site")}</Label>
              <Select
                options={sites.map((s) => ({ value: s.site_id, label: s.name }))}
                value={form.siteId ? { value: form.siteId, label: selectedSite?.name } : null}
                onChange={(opt) => update({ siteId: opt?.value, spaceIds: ["ALL"] })}
                placeholder={t("Select a site...")}
                className="react-select-lg"
                classNamePrefix="rs"
              />
            </FormGroup>

            <FormGroup>
              <Label className="form-label-lg">{t("Spaces")}</Label>
              <Select
                isMulti
                options={[
                  { value: "ALL", label: t("All Spaces") },
                  ...spaces.map((s) => ({ value: s.space_id, label: s.name })),
                ]}
                value={
                  form.spaceIds.includes("ALL")
                    ? [{ value: "ALL", label: t("All Spaces") }]
                    : form.spaceIds.map((id) => ({
                      value: id,
                      label: spaces.find((s) => s.space_id === id)?.name || `#${id}`,
                    }))
                }
                onChange={(opts) => {
                  const vals = opts?.map((o) => o.value) || [];
                  update({ spaceIds: vals.includes("ALL") ? ["ALL"] : vals });
                }}
                isDisabled={!form.siteId}
                placeholder={t("Select spaces...")}
                className="react-select-lg"
                classNamePrefix="rs"
              />
              <small className="text-muted mt-1 d-block">
                {form.spaceIds.includes("ALL") && spaces.length > 0
                  ? t("{{count}} spaces will display this splash", { count: spaces.length })
                  : null}
              </small>
            </FormGroup>
          </div>
        );

      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <h4 className="step-title">{t("Customize your message")}</h4>
              <p className="step-description">{t("Set the welcome text and menu button label")}</p>
            </div>

            <FormGroup className="mb-4">
              <Label className="form-label-lg">{t("Welcome Title")}</Label>
              <Input
                type="text"
                value={form.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder={t("e.g. Welcome to our restaurant!")}
                className="form-control-lg"
              />
              <small className="text-muted">{t("Leave empty to show space name")}</small>
            </FormGroup>

            <FormGroup className="mb-4">
              <Label className="form-label-lg">{t("Subtitle")}</Label>
              <Input
                type="textarea"
                rows={3}
                value={form.subtitle}
                onChange={(e) => update({ subtitle: e.target.value })}
                placeholder={t("A short description...")}
              />
            </FormGroup>

            <FormGroup className="mb-4">
              <Label className="form-label-lg">{t("Menu Button Text")}</Label>
              <Input
                type="text"
                value={form.menuButtonCaption}
                onChange={(e) => update({ menuButtonCaption: e.target.value })}
                placeholder="Check menu"
                className="form-control-lg"
              />
            </FormGroup>

            <div className="custom-switch-card">
              <div className="switch-info">
                <span className="switch-label">{t("Redirect to external URL")}</span>
                <span className="switch-desc">{t("Use a custom menu link instead of the app")}</span>
              </div>
              <div className="form-check form-switch">
                <Input
                  type="checkbox"
                  className="form-check-input lg"
                  checked={form.redirectToMenu}
                  onChange={(e) => update({ redirectToMenu: e.target.checked })}
                />
              </div>
            </div>

            {form.redirectToMenu && (
              <FormGroup className="mt-3 fade-in">
                <Input
                  type="url"
                  value={form.menuUrl}
                  onChange={(e) => update({ menuUrl: e.target.value })}
                  placeholder="https://menu.example.com"
                  className="form-control-lg"
                />
              </FormGroup>
            )}
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <h4 className="step-title">{t("Secondary action button")}</h4>
              <p className="step-description">{t("Add an optional quick-action button below the menu")}</p>
            </div>

            <div className="action-options">
              {[
                {
                  value: "CALL_WAITER",
                  label: t("Call Waiter"),
                  desc: t("Quick button to request assistance"),
                  icon: <ConciergeBell size={24} />,
                  recommended: true,
                },
                {
                  value: "CUSTOM",
                  label: t("Custom Service"),
                  desc: t("Link to a specific service item"),
                  icon: <Sparkles size={24} />,
                },
                {
                  value: "NONE",
                  label: t("No Button"),
                  desc: t("Show only the menu button"),
                  icon: <EyeOff size={24} />,
                },
              ].map((opt) => (
                <div
                  key={opt.value}
                  className={`action-option ${form.actionType === opt.value ? "active" : ""}`}
                  onClick={() => {
                    update({ actionType: opt.value });
                    if (opt.value === "CALL_WAITER") {
                      update({ actionButtonText: "Call waiter" });
                    } else if (opt.value === "CUSTOM") {
                      update({ actionButtonText: "Order" });
                    }
                  }}
                >
                  <div className="option-icon">{opt.icon}</div>
                  <div className="option-content">
                    <span className="option-label">
                      {opt.label}
                      {opt.recommended && <span className="badge-recommended">{t("Recommended")}</span>}
                    </span>
                    <span className="option-desc">{opt.desc}</span>
                  </div>
                  <div className="option-check">
                    {form.actionType === opt.value && <Check size={20} />}
                  </div>
                </div>
              ))}
            </div>

            {form.actionType !== "NONE" && (
              <div className="mt-4 fade-in">
                <FormGroup>
                  <Label className="form-label-lg">{t("Button Label")}</Label>
                  <Input
                    type="text"
                    value={form.actionButtonText}
                    onChange={(e) => update({ actionButtonText: e.target.value })}
                    placeholder={form.actionType === "CALL_WAITER" ? "Call waiter" : "Order"}
                    className="form-control-lg"
                  />
                </FormGroup>
              </div>
            )}

            {form.actionType === "CUSTOM" && (
              <div className="mt-4 p-3 bg-light rounded-3 fade-in">
                <FormGroup className="mb-3">
                  <Label>{t("Select Menu")}</Label>
                  <Select
                    options={menus.map((m) => ({ value: m.menu_id, label: m.name }))}
                    value={
                      form.selectedMenuId
                        ? { value: form.selectedMenuId, label: menus.find((m) => m.menu_id === form.selectedMenuId)?.name }
                        : null
                    }
                    onChange={(opt) => update({ selectedMenuId: opt?.value, selectedMenuItemId: null })}
                    placeholder={t("Select menu...")}
                    classNamePrefix="rs"
                  />
                </FormGroup>

                {form.selectedMenuId && serviceItems.length > 0 && (
                  <FormGroup className="mb-0">
                    <Label>{t("Select Service Item")}</Label>
                    <Select
                      options={serviceItems.map((i) => ({ value: i.menu_item_id, label: i.title }))}
                      value={
                        form.selectedMenuItemId
                          ? { value: form.selectedMenuItemId, label: serviceItems.find((i) => i.menu_item_id === form.selectedMenuItemId)?.title }
                          : null
                      }
                      onChange={(opt) => update({ selectedMenuItemId: opt?.value })}
                      placeholder={t("Select item...")}
                      classNamePrefix="rs"
                    />
                  </FormGroup>
                )}

                {form.selectedMenuId && serviceItems.length === 0 && (
                  <div className="alert alert-warning mb-0">
                    {t("No SERVICE items found in this menu")}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div className="step-header text-center">
              <div className="confirm-icon">
                <Sparkles size={48} />
              </div>
              <h4 className="step-title">{t("Ready to create!")}</h4>
              <p className="step-description">{t("Review your splash screen configuration")}</p>
            </div>

            <div className="summary-card">
              <div className="summary-row">
                <span className="summary-label">{t("Site")}</span>
                <span className="summary-value">{selectedSite?.name || "-"}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">{t("Spaces")}</span>
                <span className="summary-value">
                  {form.spaceIds.includes("ALL")
                    ? t("All ({{count}})", { count: spaces.length })
                    : t("{{count}} selected", { count: form.spaceIds.length })}
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">{t("Title")}</span>
                <span className="summary-value">{form.title || t("(Space name)")}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">{t("Action Button")}</span>
                <span className="summary-value">
                  {form.actionType === "NONE"
                    ? t("None")
                    : form.actionButtonText || "-"}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Preview component
  const Preview = () => (
    <div className="phone-preview">
      <div className="phone-frame">
        <div className="phone-screen">
          <div className="preview-content">
            {/* Logo */}
            <div className="preview-logo">
              {selectedSite?.image_url ? (
                <img src={selectedSite.image_url} alt="" />
              ) : (
                <div className="logo-placeholder">
                  <MapPin size={32} />
                </div>
              )}
            </div>

            {/* Site name */}
            <h3 className="preview-site-name">{selectedSite?.name || "Your Restaurant"}</h3>

            {/* Title */}
            <p className="preview-title">
              {form.title || (spaces.length > 0 ? spaces[0]?.name : "Welcome")}
            </p>

            {/* Divider */}
            <div className="preview-divider" />

            {/* Subtitle */}
            <p className="preview-subtitle">{form.subtitle}</p>

            {/* Buttons */}
            <div className="preview-buttons">
              <button className="preview-btn primary">{form.menuButtonCaption || "Check menu"}</button>
              {form.actionType !== "NONE" && (
                <button className="preview-btn secondary">
                  <ConciergeBell size={18} />
                  {form.actionButtonText || "Call waiter"}
                </button>
              )}
            </div>
          </div>

          <div className="preview-footer">
            <span>Enabled by</span>
            <div className="clickorder-logo">
              CLICK<span className="order">ORDER</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={showAddSplashModal}
      toggle={toggleAddSplashModal}
      size="lg"
      centered
      className="splash-wizard-modal"
    >
      <ModalHeader toggle={toggleAddSplashModal} className="border-0 pb-0">
        <span className="wizard-modal-title">{t("Create Splash Screen")}</span>
      </ModalHeader>

      <ModalBody className="pt-1">
        {/* Progress bar */}
        {/* <div className="wizard-progress">
          <Progress value={progress} className="progress-thin" />
        </div> */}

        {/* Step indicators */}
        <div className="step-indicators">
          {STEPS.map((step, idx) => {
            const StepIcon = step.icon;
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            const isLastStep = idx === STEPS.length - 1;

            return (
              <React.Fragment key={step.id}>
                <div
                  className={`step-indicator ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                  onClick={() => idx <= currentStep && setCurrentStep(idx)}
                >
                  <div className="indicator-circle">
                    {isCompleted ? <Check size={18} /> : <StepIcon size={18} />}
                  </div>
                  <span className="indicator-label">{step.title}</span>
                </div>
                {!isLastStep && (
                  <div className={`step-connector ${idx < currentStep ? "completed" : ""}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Main content area */}
        <div className="wizard-body">
          {/* Form section */}
          <div className={`wizard-form ${direction}`} ref={contentRef}>
            {renderStepContent()}
          </div>

          {/* Preview section */}
          <div className={`wizard-preview ${showPreview ? "show" : "hide"}`}>
            <div className="preview-toggle d-lg-none" onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
              {showPreview ? t("Hide Preview") : t("Show Preview")}
            </div>
            <Preview />
          </div>
        </div>

        {/* Footer buttons */}
        <div className="wizard-footer">
          <Button
            color="link"
            className="btn-back"
            onClick={goPrev}
            disabled={currentStep === 0}
          >
            <ChevronLeft size={18} />
            {t("Back")}
          </Button>

          <div className="footer-right">
            <Button color="light" onClick={toggleAddSplashModal} disabled={saving}>
              {t("Cancel")}
            </Button>
            <Button
              color={currentStep === STEPS.length - 1 ? "success" : "primary"}
              onClick={goNext}
              disabled={!isStepValid() || saving}
              className="btn-next"
            >
              {saving ? (
                <Spinner size="sm" />
              ) : currentStep === STEPS.length - 1 ? (
                <>
                  <Check size={18} />
                  {t("Create Splash")}
                </>
              ) : (
                <>
                  {t("Continue")}
                  <ChevronRight size={18} />
                </>
              )}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddSplash;
